import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import toColorString from 'polished/lib/color/toColorString';
import { color } from '@decisiv/design-tokens';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Tag from '@decisiv/ui-components/lib/components/Tag';
import ChevronRight from '@decisiv/iconix/lib/components/ChevronRight';
import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import Flag from '@decisiv/iconix/lib/components/Flag';

import { formatTimestamp, truncate } from '../utils/formatters';
import {
  RowContainer,
  SummaryLine,
  Timestamp,
  LevelBadge,
  HttpInfo,
  ControllerAction,
  PathText,
  MessageText,
  DetailContainer,
  JsonBlock,
  BacktraceBlock,
  SectionLabel,
  SyntaxStyles,
} from './styledComponents';

const LEVEL_COLORS = {
  ERROR: { border: color.status.danger.medium, text: color.status.danger.dark },
  FATAL: { border: color.status.danger.medium, text: color.status.danger.dark },
  WARN: {
    border: color.status.warning.medium,
    text: color.status.warning.dark,
  },
  WARNING: {
    border: color.status.warning.medium,
    text: color.status.warning.dark,
  },
  INFO: {
    border: color.status.success.medium,
    text: color.status.success.dark,
  },
  DEBUG: { border: color.base.alaskanHusky, text: color.base.alaskanHusky },
};

function getLevelStyle(level) {
  return LEVEL_COLORS[level] || LEVEL_COLORS.INFO;
}

function syntaxHighlight(jsonString) {
  const escaped = jsonString
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = 'json-number';
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'json-key';
        } else {
          cls = 'json-string';
        }
      } else if (/true|false/.test(match)) {
        cls = 'json-boolean';
      } else if (/null/.test(match)) {
        cls = 'json-null';
      }
      return `<span class="${cls}">${match}</span>`;
    },
  );
}

// Log events have dynamic fields with no fixed schema
/* eslint-disable react/prop-types */
function EventRow({ event, isAnchor }) {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setExpanded((prev) => !prev);
    }
  }, []);

  const handleDetailClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Extract fields using the names Loki::ErrorLogSearch normalizes events to
  const level = (
    event.Level ||
    event.level ||
    event.loglevel ||
    ''
  ).toUpperCase();
  const levelStyle = getLevelStyle(level);
  const borderColor = isAnchor
    ? color.status.warning.medium
    : levelStyle.border;

  const ts = formatTimestamp(event['@timestamp'] || event.time || '');
  const controller = event.controller || '';
  const action = event.action || '';
  const controllerAction =
    controller && action ? `${controller}#${action}` : '';
  const status = event.status || '';
  const method = event.method || '';
  const path = event.path || '';
  const msg = event.message || event['@rawstring'] || '';
  const backtrace = event.backtrace || '';
  const rawJson = event.RawJson || event.rawjson || '';

  const eventJson = useMemo(() => JSON.stringify(event, null, 2), [event]);

  const highlightedJson = useMemo(() => syntaxHighlight(eventJson), [
    eventJson,
  ]);

  const parsedRawJson = useMemo(() => {
    if (!rawJson) return null;
    try {
      const obj = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      return typeof rawJson === 'string' ? rawJson : null;
    }
  }, [rawJson]);

  const highlightedRawJson = useMemo(() => {
    if (!parsedRawJson) return null;
    return syntaxHighlight(parsedRawJson);
  }, [parsedRawJson]);

  const ChevronIcon = expanded ? ChevronDown : ChevronRight;

  return (
    <RowContainer
      $borderColor={borderColor}
      $isAnchor={isAnchor}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
    >
      <SummaryLine alignItems="center" paddingX={1} paddingY={0.5}>
        <Flex alignItems="center" marginRight={0.5} style={{ flexShrink: 0 }}>
          <ChevronIcon
            size="small"
            color={toColorString(color.base.alaskanHusky)}
          />
        </Flex>

        <Timestamp>{ts}</Timestamp>

        {level && <LevelBadge $color={levelStyle.text}>{level}</LevelBadge>}

        {isAnchor && (
          <Flex marginRight={0.5} style={{ flexShrink: 0 }}>
            <Tag
              text="ANCHOR"
              color="warning"
              palette="bright"
              size="small"
              icon={Flag}
            />
          </Flex>
        )}

        {(method || status) && (
          <HttpInfo>{[method, status].filter(Boolean).join(' ')}</HttpInfo>
        )}

        {controllerAction && (
          <ControllerAction>{controllerAction}</ControllerAction>
        )}

        {path && <PathText title={path}>{path}</PathText>}

        <MessageText title={msg}>{truncate(msg, 120)}</MessageText>
      </SummaryLine>

      {expanded && (
        <DetailContainer onClick={handleDetailClick}>
          <SyntaxStyles>
            <Flex flexDirection="column">
              <SectionLabel marginBottom={0.5}>Event JSON</SectionLabel>
              <JsonBlock
                dangerouslySetInnerHTML={{ __html: highlightedJson }}
              />

              {highlightedRawJson && (
                <Flex flexDirection="column" marginTop={1.5}>
                  <SectionLabel marginBottom={0.5}>Raw JSON</SectionLabel>
                  <JsonBlock
                    dangerouslySetInnerHTML={{ __html: highlightedRawJson }}
                  />
                </Flex>
              )}

              {backtrace && (
                <Flex flexDirection="column" marginTop={1.5}>
                  <SectionLabel marginBottom={0.5}>Backtrace</SectionLabel>
                  <BacktraceBlock>
                    {Array.isArray(backtrace)
                      ? backtrace.join('\n')
                      : backtrace}
                  </BacktraceBlock>
                </Flex>
              )}
            </Flex>
          </SyntaxStyles>
        </DetailContainer>
      )}
    </RowContainer>
  );
}

EventRow.propTypes = {
  event: PropTypes.shape({}).isRequired,
  isAnchor: PropTypes.bool,
};

EventRow.defaultProps = {
  isAnchor: false,
};

/* eslint-enable react/prop-types */

export default EventRow;
